import React, { PureComponent } from "react";
import SweetAlert from "sweetalert-react";

import Auth from "../routes/Auth";
import DepartmentSignoffs from "../components/ceo/decisions/department-signoffs";
import ExpandCollapsePage from "../components/ceo/decisions/expand-collapse-page";
import ApprovalService from "../api/ApprovalService";
import { logError } from "../services/utils";
import EventEmitter from "../services/event-emitter";
import { batchRequest } from "../api/ApiService";
import {
  submitDecisions,
  getSubmissionRequest,
  handleSubmission,
  updateSubmissionFromJournal
} from "../api/SubmitDecisions";
import RoleButtons from "../components/role-buttons";
import { view } from "@risingstack/react-easy-state";
import LastStepModal from "../components/get-started/last-step";

function capitalizeFirstLetter(s) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const CEO_DECISIONS_URL = `${process.env.REACT_APP_API_URL}team/page/ceo/`;
const CEO_PROJECTIONS_URL = `${process.env.REACT_APP_API_URL}team/projections/`;
const OPERATIONS_DECISIONS_URL = `${process.env.REACT_APP_API_URL}team/page/operations/`;
const FINANCE_DECISIONS_URL = `${process.env.REACT_APP_API_URL}team/page/financials/`;

class CeoDecisions extends PureComponent {
  constructor(props) {
    super(props);

    let has_access = Auth.getRole() === "CEO";

    this.api = new ApprovalService();

    this.state = {
      hasAccess: has_access,
      roleAlert: false,
      approvals: this.api.approvals,
      approveColor: "red",
      currentRound: props.currentRound,
      roundInUrl: props.roundInUrl,
      rounds: props.rounds,
      projections: [],
      loaded: false,
      spending: {
        pd: 0,
        pm: 0,
        op: 0,
        fn: 0
      },
      submission_data: {
        pd: {
          date: "N/A",
          unaccepted: 0
        },
        pm: {
          date: "N/A",
          unaccepted: 0
        },
        op: {
          date: "N/A",
          unaccepted: 0
        },
        fn: {
          date: "N/A",
          unaccepted: 0
        }
      },
      financial_decisions: null,
      unaccepted_decisions: null,
      submitDecisionsLoadingDevelopment: true,
      submitDecisionsLoadingMarketing: true,
      submitDecisionsLoadingOperations: true,
      submitDecisionsLoadingFinance: true,
      approveLoadingDevelopment: true,
      approveLoadingMarketing: true,
      approveLoadingOperations: true,
      approveLoadingFinance: true,
      isPDPreChecked: false,
      isPMPreChecked: false,
      isOPPreChecked: false,
      isFNPreChecked: false,
    };

    this.showRoleAlert = this.showRoleAlert.bind(this);
    this.hideRoleAlert = this.hideRoleAlert.bind(this);

    this.eventemitter = EventEmitter.getInstance();
  }

  reloadRole = () => {
    const role = Auth.getRole();
    const hasAccess = role === "CEO";

    this.setState({
      hasAccess
    });
  };

  componentDidMount() {
    this.eventemitter.on("ceo-approve-red", this.setApproveRed);
    this.eventemitter.on("ceo-approve-green", this.setApproveGreen);
    this.eventemitter.on("update-submit-decisions", this.updateSubmitDecisions);
    this.fetchData();
  }

  componentWillReceiveProps(nextProps) {
    if (
      this.state.roundInUrl !== nextProps.roundInUrl ||
      this.state.currentRound !== nextProps.currentRound ||
      (this.state.rounds &&
        this.state.rounds.length === 0 &&
        nextProps.rounds &&
        nextProps.rounds.length !== 0)
    ) {
      this.setState(
        {
          roundInUrl: nextProps.roundInUrl,
          currentRound: nextProps.currentRound,
          rounds: nextProps.rounds
        },
        () => {
          this.fetchData();
          this.reloadRole();
        }
      );
    }
  }

  componentWillUnmount() {
    this.eventemitter.off("ceo-approve-red", this.setApproveRed);
    this.eventemitter.off("ceo-approve-green", this.setApproveGreen);
    this.eventemitter.off("update-submit-decisions", this.updateSubmitDecisions);
  }

  updateSubmitDecisions = () => {
    const { roundInUrl, currentRound } = this.state;

    this.setState({
      submitDecisionsLoadingDevelopment: true,
      submitDecisionsLoadingMarketing: true,
      submitDecisionsLoadingOperations: true,
      submitDecisionsLoadingFinance: true
    }, () => {
      updateSubmissionFromJournal(
        ['pd', 'pm', 'op', 'fn'],
        roundInUrl !== null ? roundInUrl : currentRound,
        submission_data => {
          this.setState({
            submission_data,
            submitDecisionsLoadingDevelopment: false,
            submitDecisionsLoadingMarketing: false,
            submitDecisionsLoadingOperations: false,
            submitDecisionsLoadingFinance: false
          });
        }
      );
    });
  }

  setApproveRed = () => {
    this.setState({
      approveColor: "red"
    });
  };

  setApproveGreen = () => {
    this.setState({
      approveColor: "green"
    });
  };

  getRequest = base_url => {
    const { roundInUrl, currentRound } = this.state;
    let url = base_url;
    const temporaryUser = Auth.getTemporaryUser();
    const literal = temporaryUser ? "&" : "?";

    if (roundInUrl !== null && roundInUrl !== currentRound) {
      url = `${url}${literal}round_number=${roundInUrl}`;
    } else {
      url = `${url}${literal}round_number=${currentRound}`;
    }

    return url;
  };

  getDecisions = () => {
    let apiURL = CEO_DECISIONS_URL;
    const temporaryUser = Auth.getTemporaryUser();
    if (temporaryUser) {
      apiURL = `${apiURL}?team_id=${temporaryUser.team_id}`;
    }

    return this.getRequest(apiURL);
  };

  getProjections = () => {
    let apiURL = CEO_PROJECTIONS_URL;
    const temporaryUser = Auth.getTemporaryUser();
    if (temporaryUser) {
      apiURL = `${apiURL}?team_id=${temporaryUser.team_id}`;
    }

    return this.getRequest(apiURL);
  };

  getOperationsDecisions = () => {
    let apiURL = OPERATIONS_DECISIONS_URL;
    const temporaryUser = Auth.getTemporaryUser();
    if (temporaryUser) {
      apiURL = `${apiURL}?team_id=${temporaryUser.team_id}`;
    }

    return this.getRequest(apiURL);
  };

  getFinancialDecisions = () => {
    let apiURL = FINANCE_DECISIONS_URL;
    const temporaryUser = Auth.getTemporaryUser();
    if (temporaryUser) {
      apiURL = `${apiURL}?team_id=${temporaryUser.team_id}`;
    }

    return this.getRequest(apiURL);
  };

  get roundNumber() {
    const { roundInUrl, currentRound } = this.state;

    if (roundInUrl !== null && roundInUrl !== currentRound) {
      return roundInUrl;
    }

    return currentRound;
  }

  loadSubmissionData = (roundNumber, round, temporaryUser) => {
    const requests = getSubmissionRequest(
      ["pd", "pm", "op", "fn"],
      roundNumber,
      round.is_finished,
      temporaryUser,
    );

    batchRequest(requests)
      .then(res => {
        const unaccepted_decisions = res[0].body;

        this.setState({
          unaccepted_decisions,
        }, () => {
          if (round.is_finished) {
            this.updateSubmitDecisions();
          } else {
            const submission_data = handleSubmission(
              ["pd", "pm", "op", "fn"],
              [
                unaccepted_decisions,
                res[1].body,
                res[2].body,
                res[3].body,
                res[4].body
              ],
              round.is_finished
            );

            this.setState({
              submission_data,
              submitDecisionsLoadingDevelopment: false,
              submitDecisionsLoadingMarketing: false,
              submitDecisionsLoadingOperations: false,
              submitDecisionsLoadingFinance: false
            });
          }
        });
      })
      .catch(err => { });
  }

  fetchData = () => {
    const { rounds } = this.state;
    const roundNumber = this.roundNumber;

    if (rounds.length === 0) {
      return;
    }

    const round = rounds.find(i => i.number === roundNumber);
    if (!round) {
      return;
    }

    const temporaryUser = Auth.getTemporaryUser();
    let apiURL = this.api.url;
    if (temporaryUser) {
      apiURL = `${apiURL}?team_id=${temporaryUser.team_id}`;
    }

    const joinRequest = batchRequest([
      apiURL,
      this.getDecisions(),
      this.getProjections(),
      this.getOperationsDecisions(),
      this.getFinancialDecisions(),
      `${process.env.REACT_APP_API_URL}v2/distributions/team-centers/`,
      `${process.env.REACT_APP_API_URL}games/${Auth.getGameId()}/journal/?round_number=${roundNumber}${temporaryUser ? `&team_id=${temporaryUser.team_id}` : ''}`,
    ]);

    joinRequest
      .then(res => {
        const approvals = this.api.initialize(res[0].body);
        const decisions = res[1].body;
        const projections = res[2].body;
        const operations_decisions = res[3].body;
        const financial_decisions = res[4].body;
        const roundId = round ? Number(round.id) : 0;
        const teamId = Number(Auth.getTeamId());
        const distributionCentersRaw = res[5].body;
        const distributionCenterObj = distributionCentersRaw.results.find(i => (
          Number(i.round) === roundId
          && Number(i.team) === teamId
        ));
        const distributionCenter = distributionCenterObj ? Number(distributionCenterObj.distribution_center) : 0;

        let spending = {
          pd: 0,
          pm: 0,
          op: 0,
          fn: 0
        };

        spending.pd = decisions.team.product_development_role;
        spending.pm = decisions.team.product_marketing_role;

        const {
          production_costs,
          machines,
          facility_costs,
          capital_exp_sales,
          cash_payments_prior_round_purchases,
          distribution_centers,
        } = operations_decisions.team;
        let machines_costs = 0;
        machines.forEach(machine => {
          machines_costs += machine.machine_costs;
        });

        const distributionCenterCost = distribution_centers
          ? distribution_centers.find(i => (
            Number(i.type) === distributionCenter
            && Number(i.round) === roundId
          ))
          : null;
        const cash_payment_for_distribution_center = distributionCenterCost ? Number(distributionCenterCost.cost) : 0;

        spending.op =
          machines_costs +
          production_costs -
          facility_costs +
          capital_exp_sales +
          cash_payments_prior_round_purchases +
          cash_payment_for_distribution_center;
        spending.fn = decisions.team.finance_role;

        const journal = res[6].body;

        const pathFound1 = journal.filter(i => i.path.indexOf('/api/team/price_forecasts/') === 0).length > 0;
        const pathFound2 = journal.filter(i => i.path.indexOf('/api/team/promos/') === 0).length > 0;
        const pathFound3 = journal.filter(i => i.path.indexOf('/api/team/sales/') === 0).length > 0;
        const isPMPreChecked = pathFound1 || pathFound2 || pathFound3;

        let isOPPreChecked = true;
        let historyOperations = journal.filter(i => (
          i.round_number === roundNumber &&
          (i.path.startsWith('/api/team/factories/')
            || i.path.startsWith('/api/team/machines/')
            || i.path.startsWith('/api/team/product_manufactures/')
            || i.path.startsWith('/api/teams/submit_operations/'))
        ));

        historyOperations = historyOperations.sort((a, b) => {
          const dateA = new Date(a.requested_at);
          const dateB = new Date(b.requested_at);

          return dateB - dateA;
        });

        if (!historyOperations.length || historyOperations[0].path !== '/api/teams/submit_operations/' || (historyOperations[0].status_code !== 200 && historyOperations[0].status_code !== 201))
          isOPPreChecked = false;

        const pathFound7 = journal.filter(i => i.path.indexOf('/api/team/equities/') === 0).length > 0;
        const pathFound8 = journal.filter(i => i.path.indexOf('/api/team/debts/') === 0).length > 0;
        const pathFound9 = journal.filter(i => i.path.indexOf('/api/team/debt_repayments/') === 0).length > 0;
        const pathFound10 = journal.filter(i => i.path.indexOf('/api/team/dividends_payment/') === 0).length > 0;
        const pathFound11 = journal.filter(i => i.path.indexOf('/api/team/stock_repurchased/') === 0).length > 0;
        const isFNPreChecked = pathFound7 || pathFound8 || pathFound9 || pathFound10 || pathFound11;

        let isPDPreChecked = true;

        let historySubmitDevelopment = journal.filter(i => (
          i.round_number === roundNumber &&
          (i.path.startsWith('/api/team/products/') || i.path.startsWith('/api/teams/submit_development/'))
        ));

        historySubmitDevelopment = historySubmitDevelopment.sort((a, b) => {
          const dateA = new Date(a.requested_at);
          const dateB = new Date(b.requested_at);

          return dateB - dateA;
        });

        if (!historySubmitDevelopment.length || historySubmitDevelopment[0].path !== '/api/teams/submit_development/' || (historySubmitDevelopment[0].status_code !== 200 && historySubmitDevelopment[0].status_code !== 201))
          isPDPreChecked = false;

        this.setState({
          approvals,
          spending,
          financial_decisions,
          projections,
          loaded: true,
          approveLoadingDevelopment: false,
          approveLoadingMarketing: false,
          approveLoadingOperations: false,
          approveLoadingFinance: false,
          isPDPreChecked,
          isPMPreChecked,
          isOPPreChecked,
          isFNPreChecked
        }, () => {
          this.loadSubmissionData(roundNumber, round, temporaryUser);
        });
      })
      .catch(err => {
        logError(err, "CeoDecisions", "fetchData");
      });
  };

  showRoleAlert() {
    this.setState({
      roleAlert: true
    });
  }

  hideRoleAlert() {
    this.setState({
      roleAlert: false
    });
  }

  get showActionButtons() {
    if (this.state.hasAccess === false) {
      return false;
    }

    if (this.props.roundShouldBeFinished === true) {
      return false;
    }

    const { roundInUrl, currentRound, rounds } = this.state;

    if (roundInUrl !== null && roundInUrl !== currentRound) {
      return false;
    }

    const round = rounds.find(i => i.number === currentRound);

    if (!round) {
      return false;
    }

    return round.is_started === true && round.is_finished === false;
  }

  get productDevelopmentStatus() {
    return this.api.productDevelopmentStatus;
  }

  get productMarketingStatus() {
    return this.api.productMarketingStatus;
  }

  get operationsStatus() {
    return this.api.operationsStatus;
  }

  get financeStatus() {
    return this.api.financeStatus;
  }

  get productDevelopmentDisabled() {
    return this.api.productDevelopment.length > 0 ? false : true;
  }

  get productMarketingDisabled() {
    return this.api.productMarketing.length > 0 ? false : true;
  }

  get operationsDisabled() {
    return this.api.operations.length > 0 ? false : true;
  }

  get financeDisabled() {
    return this.api.finance.length > 0 ? false : true;
  }

  updateProductDevelopment = is_approved => {
    if (this.props.tourMode === true) {
      return;
    }

    if (this.state.hasAccess === false) {
      this.eventemitter.trigger("app-show-error", [
        "Only the CEO can approve a decision."
      ]);
      return;
    }

    if (this.productDevelopmentDisabled === true) {
      this.eventemitter.trigger("app-show-error", ["No decisions to approve."]);
      return;
    }

    this.setState({
      approveLoadingDevelopment: true
    });

    this.api.updateProductDevelopment(is_approved).then(approvals => {
      this.setState({
        approvals,
        approveLoadingDevelopment: false
      });
    });
  };

  updateProductMarketing = is_approved => {
    if (this.props.tourMode === true) {
      return;
    }

    if (this.state.hasAccess === false) {
      this.eventemitter.trigger("app-show-error", [
        "Only the CEO can approve a decision."
      ]);
      return;
    }

    if (this.productMarketingDisabled === true) {
      this.eventemitter.trigger("app-show-error", ["No decisions to approve."]);
      return;
    }

    this.setState({
      approveLoadingMarketing: true
    });

    this.api.updateProductMarketing(is_approved).then(approvals => {
      this.setState({
        approvals,
        approveLoadingMarketing: false
      });
    });
  };

  updateOperations = is_approved => {
    if (this.props.tourMode === true) {
      return;
    }

    if (this.state.hasAccess === false) {
      this.eventemitter.trigger("app-show-error", [
        "Only the CEO can approve a decision."
      ]);
      return;
    }

    if (this.operationsDisabled === true) {
      this.eventemitter.trigger("app-show-error", ["No decisions to approve."]);
      return;
    }

    this.setState({
      approveLoadingOperations: true
    });

    this.api.updateOperations(is_approved).then(approvals => {
      this.setState({
        approvals,
        approveLoadingOperations: false
      });
    });
  };

  updateFinance = is_approved => {
    if (this.props.tourMode === true) {
      return;
    }

    if (this.state.hasAccess === false) {
      this.eventemitter.trigger("app-show-error", [
        "Only the CEO can approve a decision."
      ]);
      return;
    }

    if (this.financeDisabled === true) {
      this.eventemitter.trigger("app-show-error", ["No decisions to approve."]);
      return;
    }

    this.setState({
      approveLoadingFinance: true
    });

    this.api.updateFinance(is_approved).then(approvals => {
      this.setState({
        approvals,
        approveLoadingFinance: false
      });
    });
  };

  getSubmissionData = type => {
    this.setState({
      [`submitDecisionsLoading${type}`]: true
    });

    this.fetchData();
  };

  submitDecisions = type => {
    const t = capitalizeFirstLetter(type);
    this.setState({
      [`submitDecisionsLoading${t}`]: true
    });

    submitDecisions(type, this.props.roundInUrl || this.props.currentRound, () => this.getSubmissionData(t));
  };

  render() {
    const showActionButtons = this.showActionButtons;
    const {
      roundShouldBeFinished,
      tourMode,
      location,
      router,
      currentRound: round
    } = this.props;
    const {
      hasAccess,
      roleAlert,
      spending,
      submission_data,
      submitDecisionsLoadingDevelopment,
      submitDecisionsLoadingMarketing,
      submitDecisionsLoadingOperations,
      submitDecisionsLoadingFinance,
      rounds,
      currentRound,
      roundInUrl,
      loaded,
      projections,
      financial_decisions,
      unaccepted_decisions,
      isPDPreChecked,
      isPMPreChecked,
      isOPPreChecked,
      isFNPreChecked
    } = this.state;

    return (
      <section className="decisions decisions-old">
        <LastStepModal
          location={location}
          router={router}
          url={`/decisions/ceo?round=${round}`}
        />
        <SweetAlert
          show={roleAlert}
          title="Information"
          text="Unfortunately, you are not authorized to make this decision"
          type="info"
          onConfirm={this.hideRoleAlert}
        />
        <div className="col-md-12 col-xs-12 decisions-title-container">
          <h2 id="ceo-decisions-title" className="Regular">
            CEO Decisions
          </h2>
          {hasAccess && <RoleButtons type="ceo" />}
        </div>
        <div className="row">
          <div id="ceo-decisions-approve" className="col-md-7 col-xs-12">
            <DepartmentSignoffs
              spending={spending}
              submission_data={submission_data}
              submitDecisions={this.submitDecisions}
              submitDecisionsLoadingDevelopment={
                submitDecisionsLoadingDevelopment
              }
              submitDecisionsLoadingMarketing={submitDecisionsLoadingMarketing}
              submitDecisionsLoadingOperations={
                submitDecisionsLoadingOperations
              }
              submitDecisionsLoadingFinance={submitDecisionsLoadingFinance}
              showActionButtons={showActionButtons}
              financial_decisions={financial_decisions}
              unaccepted_decisions={unaccepted_decisions}
              isPDPreChecked={isPDPreChecked}
              isPMPreChecked={isPMPreChecked}
              isOPPreChecked={isOPPreChecked}
              isFNPreChecked={isFNPreChecked}
            />
          </div>
          <div className="col-md-5 col-xs-12">
            <ExpandCollapsePage
              projections={projections}
              loaded={loaded}
              hasAccess={hasAccess}
              showRoleAlert={this.showRoleAlert}
              rounds={rounds}
              currentRound={currentRound}
              roundInUrl={roundInUrl}
              reloadData={this.fetchData}
              tourMode={tourMode}
              roundShouldBeFinished={roundShouldBeFinished}
            />
          </div>
        </div>
      </section>
    );
  }
}

export default view(CeoDecisions);



// WEBPACK FOOTER //
// ./src/containers/ceo-decisions.js